import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button, Col, Form, FormGroup, Input, Label, Alert, Spinner, FormText } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import { useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faDownload, faFileAlt, faCommentDots, faInfoCircle, faSpinner, faEye } from '@fortawesome/free-solid-svg-icons';
import { get, post, patch } from '../../../../helpers/requests';
import { API, BASE_URL, STORAGE_CLIENT_KEY } from '../../../../CONST';
import { vigenciasToOptions, clientToOption } from '../../../../helpers/selects';
import PageContainer from '../../../layout/PageContainer';
import GridContainer from '../../../shared/GridContainer';
import SafeBtn from '../../../shared/SafeBtn';
import BaseModal from '../../../shared/BaseModal';
import FileTable from '../../../shared/FileTable';
import { fileDownload, userHasRole, intermediaryAccess } from '../../../../helpers/helpers';
import { ROLES_ASEGURADO, ROLES_CORREDOR } from '../../../../CONST';
import auth from '../../../../helpers/auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import { debounce } from 'lodash';
import ClientSideTable from '../../../shared/ClientSideTable';
import {
  cellOperation,
  headerOperation,
} from '../../../../helpers/tables';


export function formatDate(dateString) {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };


const SolicitudProrroga = () => {
  const loginState = useSelector((state) => state.login);
  const user = useSelector((state) => state.login.user);
  const loginIdToken = useSelector((state) => state.login.id_token);
  const [documentos, setDocumentos] = useState([]);
  const { policyId: policyIdFromParams } = useParams();
  const location = useLocation();
  const vigenciaFromState = location.state?.vigencia;
  // Corredor-specific state
  const isCorredor = intermediaryAccess(user);
  const corredorPolizas = useSelector((state) => state.credito.list.corredorPolizas);
  const selectedPolizaFromState = location.state?.selectedPoliza ?? null;

  useEffect(() => {
    if (isCorredor) {
      loadPolizas();
    }
  }, [isCorredor]);

  const [clientesOptions, setClientesOptions] = useState([]);
  const storedClient = sessionStorage.getItem(STORAGE_CLIENT_KEY);
  const [selectedClient, setSelectedClient] = useState(
    storedClient ? JSON.parse(storedClient) : null
  );
  const [polizasOptions, setPolizasOptions] = useState(
    corredorPolizas.length > 0 ? corredorPolizas : []
  );
  const [selectedPoliza, setSelectedPoliza] = useState(selectedPolizaFromState);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingPolizas, setLoadingPolizas] = useState(false);
  
  // Determine the active policyId (from params for Asegurado, from selection for Corredor)
  const policyId = isCorredor ? selectedPoliza?.id : policyIdFromParams;
  
  const [poliza, setPoliza] = useState({ nemotecnico: '', moneda: '' });
  const [vigencias, setVigencias] = useState({ all: [], selected: null });
  const [solicitandoProrroga, setSolicitandoProrroga] = useState(false);
  const [prorrogas, setProrrogas] = useState([]);
  const [comentarios, setComentarios] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedProrroga, setSelectedProrroga] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', color: 'success' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [initialComment, setInitialComment] = useState('');
  const [loadingProrrogas, setLoadingProrrogas] = useState(false);
  const [subjectMail, setSubjectMail] = useState('');
  const allVigenciasOpts = vigenciasToOptions(vigencias.all);
  const selectedVigenciaOpt = vigencias.selected 
    ? allVigenciasOpts.find((opt) => opt.value.id === vigencias.selected.id)
    : null;

  // Load clientes for Corredor
  useEffect(() => {
    if (isCorredor) {
      loadClientes();
    }
  }, [isCorredor]);

  // Load polizas when client is selected (Corredor) or on mount (Asegurado)
  useEffect(() => {
    if (isCorredor && selectedClient) {
      loadPolizas();
    }
  }, [selectedClient, isCorredor]);

  useEffect(() => {
    if (policyId) {
      fetchData();
    }
  }, [policyId]);


  const [valueFilterEstado, setValueFilterEstado] = useState('pendientes');
  const [activeFilters, setActiveFilters] = useState({status_filter: 'pendientes'});
  
  const debouncedStatusFilterChange = debounce((value) => {
    const newFilters = { ...activeFilters };
    if (value) {
      newFilters.status_filter = value;
    } else {
      delete newFilters.status_filter;
    }
    setActiveFilters(newFilters);
    setValueFilterEstado(value);
  }, 300);

  const loadClientes = async () => {
    try {
      setLoadingClientes(true);
      const url = `${BASE_URL}${API.clientesIntermediario}/?ramo=credito`;
      const response = await auth.fetch(url);
      const options = response.map((client) => clientToOption(client));
      setClientesOptions(options);
      
      // If there's a stored client, select it
      if (selectedClient) {
        const storedClientOption = options.find(opt => opt.value.id === selectedClient.id);
        if (storedClientOption) {
          setSelectedClient(storedClientOption.value);
        }
      }
    } catch (error) {
      showAlert('Error al cargar los clientes', 'danger');
    } finally {
      setLoadingClientes(false);
    }
  };

  const loadPolizas = async () => {
    if (corredorPolizas.length > 0) {
      if (policyIdFromParams && !selectedPolizaFromState) {
        const match = corredorPolizas.find(
          (opt) => opt.value.id === parseInt(policyIdFromParams, 10)
        );
        if (match) handlePolizaChange(match);
      }
      return;
    }

    try {
      setLoadingPolizas(true);
      const url = `${BASE_URL}${API.creditos}?intermediario_id=${user.organization}`;
      const response = await get(url);
      const polizas = response.results || response;

      const options = polizas.map((poliza) => ({
        label: `${poliza.nemotecnico} - ${poliza.balance.asegurado.nombre}`,
        value: poliza,
      }));

      setPolizasOptions(options);

      if (options.length === 1) {
        handlePolizaChange(options[0]);
        return;
      }

      if (policyIdFromParams) {
        const matchingOption = options.find(
          (opt) => opt.value.id === parseInt(policyIdFromParams, 10)
        );
        if (matchingOption) handlePolizaChange(matchingOption);
      }
    } catch (error) {
      console.error('Error loading policies:', error);
      showAlert('Error al cargar las pólizas', 'danger');
    } finally {
      setLoadingPolizas(false);
    }
  };


  const fetchData = async () => {
    if (!policyId) return;
    
    try {
      setLoadingProrrogas(true);
      const vigenciasData = await get(`${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${policyId}&get_last_including_borrador=true`);

      const mainVigencias = vigenciasData.filter(endoso => 
        endoso.vigencia === null && endoso.estado === 'emitido'
      );

      const preSelected = vigenciaFromState
        ? mainVigencias.find(v => v.numero_vigencia === vigenciaFromState) ?? mainVigencias[0]
        : mainVigencias[0];

      setVigencias({
        all: mainVigencias,
        selected: preSelected,
      });
      setPoliza({
        nemotecnico: mainVigencias[0]?.nemotecnico || ''
      });
    } catch (error) {
      showAlert('Error al cargar los datos', 'danger');
    } finally {
      setLoadingProrrogas(false);
    }
  };

  const fetchProrrogas = async () => {
    if (!vigencias.selected) return;
  
    try {
      setLoadingProrrogas(true);
    
      let url = `${BASE_URL}${API.simpleProrroga}/?poliza_id=${policyId}&vigencia=${vigencias.selected.numero_vigencia}`;
      if (activeFilters.status_filter) {
        url += `&status_filter=${encodeURIComponent(activeFilters.status_filter)}`;
      }
    
      let allResults = [];
      let nextUrl = url;
    
      while (nextUrl) {
        const pageData = await get(nextUrl);
        allResults = allResults.concat(pageData.results);
        nextUrl = pageData.links?.next || null;
      }
    
      setProrrogas(allResults);
    } catch (error) {
      console.log("Error fetching prórrogas: ", error);
      showAlert('Error al cargar las prórrogas', 'danger');
    } finally {
      setLoadingProrrogas(false);
    }
  };

  const handleSolicitarClick = () => {
    if (!policyId) {
      showAlert('Por favor seleccione una póliza', 'warning');
      return;
    }
    if (!vigencias.selected) {
      showAlert('Por favor seleccione una vigencia', 'warning');
      return;
    }
    setShowCreateModal(true);
  };

  const confirmCreateProrroga = async () => {
    setSolicitandoProrroga(true);
    try {
      const prorrogaResponse = await post(`${BASE_URL}${API.simpleProrroga}/`, {
        poliza_id: policyId,
        vigencia: vigencias.selected.numero_vigencia,
        created_by: parseInt(loginState.user.sub, 10),
        asunto_correo: subjectMail
      });
    
      const commentResponse = await post(`${BASE_URL}${API.simpleProrrogaComentarios}/`, {
        simple_prorroga: prorrogaResponse.id,
        comentario: initialComment,
        documentos: documentos.map(file => ({ uuid: file.uuid })),
        created_by: parseInt(loginState.user.sub, 10),
      });

      setComentarios([...comentarios, commentResponse]);
      fetchProrrogas();
      setShowCreateModal(false);
      setInitialComment('');
      setDocumentos([]);
      showAlert('Prórroga solicitada correctamente', 'success');
    } catch (error) {
      showAlert('Error al solicitar la prórroga', 'danger');
    } finally {
      setSolicitandoProrroga(false);
    }
  };

  const fetchComments = async (prorrogaId) => {
    try {
      const commentsData = await get(`${BASE_URL}${API.simpleProrrogaComentarios}/?prorroga_id=${prorrogaId}`);
      setComentarios(commentsData.reverse());
    } catch (error) {
      showAlert('Error al cargar los comentarios', 'danger');
    }
  };

  const handlePolizaChange = (selectedOption) => {
    setSelectedPoliza(selectedOption.value);
    setVigencias({ all: [], selected: null });
    setProrrogas([]);
  };

  const handleVigenciaChange = (selectedOption) => {
    setVigencias((prevState) => ({
      ...prevState,
      selected: selectedOption.value,
    }));
  };

  const addComment = async () => {
    if (!newComment.trim()) {
      showAlert('Por favor ingrese un comentario', 'warning');
      return;
    }

    const commentText = newComment;
    const filesToUpload = [...documentos];
    let prorrogaToUpdate = selectedProrroga;

    try {
      if (!selectedProrroga.is_open) {
        try {
          const updatedProrroga = await patch(`${BASE_URL}${API.simpleProrroga}/${selectedProrroga.id}/`, {
            is_open: true
          });
          
          prorrogaToUpdate = updatedProrroga;
          setSelectedProrroga(updatedProrroga);
          
          setProrrogas(prevProrrogas => 
            prevProrrogas.map(prorroga => 
              prorroga.id === selectedProrroga.id 
                ? { ...prorroga, is_open: true } 
                : prorroga
            )
          );
          
          showAlert('La prórroga ha sido reabierta automáticamente', 'info');
        } catch (error) {
          console.error('Error reopening prorroga:', error);
          showAlert('Error al reabrir la prórroga', 'danger');
          return;
        }
      }

      setNewComment('');
      setDocumentos([]);

      const response = await post(`${BASE_URL}${API.simpleProrrogaComentarios}/`, {
        simple_prorroga: prorrogaToUpdate.id,
        comentario: commentText,
        documentos: filesToUpload.map(file => ({ uuid: file.uuid })),
        created_by: parseInt(loginState.user.sub, 10),
      });

      setComentarios(prev => [...prev, response]);

      showAlert('Comentario agregado correctamente', 'success');
    } catch (error) {
      setNewComment(commentText);
      setDocumentos(filesToUpload);
      setShowCommentModal(true);

      showAlert('Error al agregar el comentario', 'danger');
      console.error('Error adding comment:', error);
    }
  };

  const showAlert = (message, color) => {
    setAlert({ show: true, message, color });
    setTimeout(() => setAlert({ show: false, message: '', color: 'success' }), 5000);
  };

  const openCommentModal = (prorroga) => {
    setSelectedProrroga(prorroga);
    fetchComments(prorroga.id);
    setShowCommentModal(true);
  };

  useEffect(() => {
    if (!showCommentModal || !selectedProrroga?.id) return;

    fetchComments(selectedProrroga.id);

    const intervalId = setInterval(() => {
      fetchComments(selectedProrroga.id);
    }, 5000); // 5 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [showCommentModal, selectedProrroga?.id]);


  useEffect(() => {
    fetchProrrogas();
  }, [vigencias.selected, activeFilters]);

  useEffect(() => { // hace que el scroll parta desde el final
    const container = document.querySelector('.prorroga-chat-container');
    if (container) container.scrollTop = container.scrollHeight;
  }, [comentarios]);

  const StatusFilter = ({ filter, onChange, loading, value }) => {
    const [selectValue, setSelectValue] = useState(value || '');

    useEffect(() => {
      setSelectValue(value || '');
    }, [value]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setSelectValue(newValue);
      onChange(newValue);
    };

    return (
      <div style={{ position: 'relative' }}>
        <select
          value={selectValue}
          onChange={handleChange}
          className="form-control form-control-sm"
          style={{ fontSize: '0.8rem', fontWeight: 'normal', minWidth: '120px' }}
        >
          <option value="">Todos</option>
          <option value="pendientes">Pendientes</option>
          <option value="cerradas">Resueltas</option>
        </select>
        {loading && (
          <div style={{ position: 'absolute', right: '24px', top: '6px', pointerEvents: 'none' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.75rem' }} />
          </div>
        )}
      </div>
    );
  };


  const breadcrumbs = isCorredor ? [
    { id: 0, name: 'SEGURO DE CRÉDITO', url: '/' },
    { id: 1, name: 'PRÓRROGAS', url: '/corredor/polizas-credito' },
    { id: 2, name: 'SOLICITUD DE PRORROGA' },
  ] : [
    { id: 0, name: 'SEGURO DE CRÉDITO', url: '/' },
    { id: 1, name: 'PRÓRROGAS', url: '/asegurado/polizas-credito' },
    { id: 2, name: 'SOLICITUD DE PRORROGA' },
  ];

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
    >
      {alert.show && (
        <Alert color={alert.color} className="mt-3">
          {alert.message}
        </Alert>
      )}
      
      <Col md={{ size: 12 }}>
        <GridContainer columns="510px" gap="40px" alignItems="top" style={{ marginTop: '20px' }}>
          <div>
            <Form>
              {isCorredor && (
                <FormGroup row className="mb-0">
                  <Label for="poliza-select" sm={5} className="orsan-label">
                    Póliza
                  </Label>
                  <Col sm={7}>
                    <Select
                      id="poliza-select"
                      name="poliza-select"
                      className="orsan-form-element"
                      value={selectedPoliza ? {
                        label: `${selectedPoliza.nemotecnico} - ${
                          selectedPoliza.balance?.asegurado?.nombre
                          ?? selectedPoliza.asegurado_nombre
                          ?? 'Sin cliente'
                        }`,
                        value: selectedPoliza,
                      } : null}
                      onChange={handlePolizaChange}
                      options={polizasOptions}
                      placeholder="Seleccione póliza"
                      isLoading={loadingPolizas}
                      isDisabled={loadingPolizas}
                    />
                  </Col>
                </FormGroup>
              )}
              {!isCorredor && (
                <FormGroup row className="mb-0">
                  <Label for="poliza" sm={5} className="orsan-label">
                    Póliza
                  </Label>
                  <Col sm={7}>
                    <Input
                      id="poliza"
                      plaintext
                      disabled
                      value={poliza.nemotecnico.length > 1 ? poliza.nemotecnico : ''}
                    />
                  </Col>
                </FormGroup>
              )}
              <FormGroup row className="mb-0">
                <Label for="poliza" sm={5} className="orsan-label">
                  Plazo gestiones propias
                </Label>
                <Col sm={7}>
                  <Input
                    id="plazo_gestiones_propias"
                    plaintext
                    disabled
                    value={
                      vigencias.selected
                        ? `${vigencias.selected.plazo_gestiones_propias} días`
                        : ''
                    }
                  />
                </Col>
              </FormGroup>
              <FormGroup row className="mb-0">
                <Label for="vigencia" sm={5} className="orsan-label">
                  Vigencia
                </Label>
                <Col sm={7}>
                  <Select
                    id="vigencia"
                    name="vigencia"
                    className="orsan-form-element"
                    value={selectedVigenciaOpt}
                    onChange={handleVigenciaChange}
                    options={allVigenciasOpts}
                    placeholder="Seleccione vigencia"
                    isDisabled={!policyId}
                  />
                </Col>
              </FormGroup>
            </Form>
          </div>
        </GridContainer>

        {/* Solicitudes de prorroga solicitadas */}
        <div style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '10px' }}>
            <span>Solicitudes de Prorroga</span>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {(!userHasRole(user, ROLES_CORREDOR) && userHasRole(user, ROLES_ASEGURADO) || user.is_orsan) && 
              <SafeBtn
                loading={solicitandoProrroga}
                onClick={() => (
                  fileDownload(`${BASE_URL}${API.prorrogasBulkUploadTemplate}/`, loginIdToken)
                )}
                color="orsan"
                style={{ width: '150px', fontSize: '14px' }}
              >
                Descargar Plantilla
              </SafeBtn>
              }
            </div>
          </div>
          {!policyId ? (
            <p>Por favor seleccione un cliente y una póliza para ver las solicitudes de prórroga</p>
          ) : (
            <ClientSideTable
              columns={[
                {
                  Header: headerOperation('Vigencia'),
                  id: 'vigencia',
                  accessor: 'vigencia',
                  Cell: (row) => cellOperation(row.value),
                  filterable: false,
                  width: 100
                },
                {
                  Header: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {headerOperation('Estado')}
                      <StatusFilter
                        onChange={debouncedStatusFilterChange}
                        value={valueFilterEstado}
                        loading={loadingProrrogas}
                      />
                    </div>
                  ),
                  id: 'estado',
                  accessor: 'is_open',
                  Cell: (row) => cellOperation(row.value ? 'Pendiente' : 'Resuelto'),
                  filterable: false,
                  sortable: false,
                  width: 200,
                },
                {
                  Header: headerOperation('Fecha Solicitud'),
                  id: 'created_ts',
                  accessor: (i) => cellOperation(formatDate(i.created_ts)),
                  filterable: false,
                  width: 210,
                },
                {
                  Header: headerOperation('Fecha Término'),
                  id: 'fecha_termino',
                  accessor: (i) =>
                    cellOperation(
                      i.fecha_termino_actual
                        ? new Date(i.fecha_termino_actual).toLocaleDateString('es-CL', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })
                        : 'Pendiente',
                    ),
                  filterable: false,
                  width: 210,
                },
                {
                  Header: headerOperation('Asunto'),
                  id: 'asunto_correo',
                  accessor: 'asunto_correo',
                  Cell: (row) => cellOperation(row.value),
                  filterable: false,
                  width: 500
                },
                {
                  Header: headerOperation('Acciones'),
                  id: 'actions',
                  accessor: (i) => (
                    <div className="table-cell" style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="table-link reactstrap-table-link"
                        onClick={() => openCommentModal(i)}
                        style={{ fontSize: '14px' }}
                      >
                        <FontAwesomeIcon icon={faEdit} /> Comentarios
                      </button>
                    </div>
                  ),
                  filterable: false,
                  sortable: false,
                  width: 120,
                },
              ]}
              data={prorrogas}
            />
          )}
        </div>
        
        <GridContainer columns="1fr auto auto" gap="10px" style={{ marginTop: '20px' }}>
          <div>
            Recuerde que las prórrogas autorizadas comienzan después del período de gestiones
            propias estipulado en la póliza.
          </div>
          <Link to={isCorredor ? "/corredor/polizas-credito" : "/asegurado/polizas-credito"}>
            <Button color="orsan-secondary" style={{ width: '150px', fontSize: '14px' }}>
              Volver
            </Button>
          </Link>
          {(!userHasRole(user, ROLES_CORREDOR) && userHasRole(user, ROLES_ASEGURADO) || user.is_orsan) && 
            <SafeBtn
              loading={solicitandoProrroga}
              onClick={handleSolicitarClick}
              color="orsan"
              style={{ width: '150px', fontSize: '14px' }}
              disabled={!policyId}
            >
              Solicitar Prórroga
            </SafeBtn>
          }
          <BaseModal
            modalOpen={showCreateModal}
            handleModal={() => setShowCreateModal(false)}
            title="Solicitar Nueva Prórroga"
            size="lg"
            type=""
          >
            <div>
              <FormGroup>
                <Label>Asunto correo</Label>
                <Input
                  type="text"
                  rows={3}
                  value={subjectMail}
                  onChange={(e) => setSubjectMail(e.target.value)}
                  placeholder="Ingrese asunto para referirse a su solicitud de prórroga"
                />
                {!subjectMail && (
                  <small className="text-danger">El asunto es obligatorio</small>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label>Comentario</Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={initialComment}
                  onChange={(e) => setInitialComment(e.target.value)}
                  placeholder="Describa el motivo de la prórroga..."
                />
                {!initialComment && (
                  <small className="text-danger">El comentario es obligatorio</small>
                )}
              </FormGroup>
              
              <FormGroup row>
                <Label for="documentos" sm={4}>Documentos</Label>
                <Col sm={8}>
                  <FileTable
                    newFiles={documentos}
                    handleAdd={(file) => {
                      setDocumentos(prev => [...prev, file]);
                    }}
                    handleDeleteNew={(file) => {
                      setDocumentos(prev => prev.filter(doc => doc.uuid !== file.uuid));
                    }}
                    loginIdToken={loginIdToken}
                    uploadUrl={`${BASE_URL}${API.uploadDocumento}/`}
                    previewFileUrl={`${BASE_URL}${API.tempFileDownload}/`}
                    previewFile
                    individualMaxFileSize={13}
                    globalMaxFileSize={20}
                  />
                </Col>
              </FormGroup>
                  
              <div className="d-flex justify-content-end mt-4">
                <Button 
                  className="btn-orsan-secondary mr-1"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="btn-orsan-primary"
                  onClick={confirmCreateProrroga}
                  disabled={solicitandoProrroga || !subjectMail || !initialComment}
                >
                  {solicitandoProrroga ? 'Enviando...' : 'Confirmar Solicitud'}
                </Button>
              </div>
            </div>
          </BaseModal>
        </GridContainer>
      </Col>

      {/* Modal comentarios */}
      <BaseModal
        modalOpen={showCommentModal}
        handleModal={() => setShowCommentModal(false)}
        title={`Comentarios para Prórroga - ${selectedProrroga?.asunto_correo || ''}`}
        type="prorroga-comments-modal"
        backdrop="static"
        size="xl"
      >
        {selectedProrroga && (
          <div className="prorroga-modal-content">
            {/* Left Column - Chat */}
            <div className="prorroga-chat-column">
              <div className="prorroga-chat-container">
                <div className="prorroga-chat-inner">
                  {comentarios.length === 0 ? (
                    <div className="prorroga-chat-empty-state">
                      <div className="prorroga-chat-empty-icon">
                        <i className="fas fa-comments"></i>
                      </div>
                      <p>No hay comentarios para esta prórroga</p>
                    </div>
                  ) : (
                    comentarios.map((comentario) => {
                      const isOrsan = comentario.is_orsan;
                      const messageType = isOrsan ? 'orsan' : 'external';

                      return (
                        <div 
                          key={comentario.id} 
                          className={`prorroga-chat-message-wrapper ${messageType}`}
                        >
                          <div className={`prorroga-chat-message ${messageType}`}>
                            {/* User info header */}
                            <div className="prorroga-chat-user-header">
                              <div>
                                <strong className={`prorroga-chat-user-name ${messageType}`}>
                                  {comentario.created_user_name || 'Usuario'}
                                  {isOrsan && (
                                    <span className="prorroga-chat-orsan-badge">
                                      ORSAN
                                    </span>
                                  )}
                                </strong>
                              </div>
                              <small className={`prorroga-chat-timestamp ${messageType}`}>
                                {formatDate(comentario.created_ts)}
                              </small>
                            </div>
                                
                            {/* Message content */}
                            <div className="prorroga-chat-content">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {comentario.comentario}
                              </ReactMarkdown>
                            </div>
                                
                            {/* Attachments */}
                            {comentario.documentos_ids?.length > 0 && (
                              <div className={`prorroga-chat-attachments ${messageType}`}>
                                <button
                                  type="button"
                                  className={`prorroga-chat-attachment-btn ${messageType}`}
                                  onClick={() =>
                                    fileDownload(
                                      `${BASE_URL}${API.simpleProrrogaComentarios}/${comentario.id}/download`,
                                      loginIdToken,
                                    )
                                  }
                                  title="Descargar archivos adjuntos"
                                >
                                  <FontAwesomeIcon icon={faDownload} className="mr-1" />
                                  Archivos ({comentario.documentos_ids.length})
                                </button>
                              </div>
                            )}

                            {/* Chat bubble tail */}
                            <div className={`prorroga-chat-tail ${messageType}`}></div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Column - New Comment Section */}
            <div className="prorroga-form-column">
              {/* Show warning if prorroga is closed */}
              {!selectedProrroga.is_open && (
                <div className="prorroga-closed-message">
                  <div>
                    <FontAwesomeIcon icon={faInfoCircle} />
                    <strong style={{ display: 'block', marginBottom: '4px' }}>Prórroga Resuelta</strong>
                    <span style={{ fontSize: '14px' }}>
                      Esta prórroga está marcada como resuelta. Al agregar un nuevo comentario,
                      se reabrirá automáticamente cambiando su estado a "Pendiente".
                    </span>
                  </div>
                </div>
              )}
              {(!userHasRole(user, ROLES_CORREDOR) && userHasRole(user, ROLES_ASEGURADO) || user.is_orsan) ? (
                <div className="prorroga-new-comment-section">
                  <FormGroup>
                    <Label className="prorroga-comment-label">
                      <FontAwesomeIcon icon={faCommentDots} className="mr-1" />
                      Nuevo Comentario
                    </Label>
                    <Input
                      type="textarea"
                      rows={8}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Escribe tu comentario aquí..."
                      className="prorroga-comment-textarea"
                    />
                    <FormText className="font-italic">Formato Markdown soportado</FormText>
                  </FormGroup>

                  <FormGroup>
                    <Label for="documentos" className="prorroga-comment-label mb-2">
                      <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                      Documentos
                    </Label>
                    <div className="prorroga-file-upload-container">
                      <FileTable
                        newFiles={documentos}
                        handleAdd={(file) => {
                          setDocumentos(prev => [...prev, file]);
                        }}
                        handleDeleteNew={(file) => {
                          setDocumentos(prev => prev.filter(doc => doc.uuid !== file.uuid));
                        }}
                        loginIdToken={loginIdToken}
                        uploadUrl={`${BASE_URL}${API.uploadDocumento}/`}
                        previewFileUrl={`${BASE_URL}${API.tempFileDownload}/`}
                        previewFile
                        individualMaxFileSize={13}
                        globalMaxFileSize={20}
                      />
                    </div>
                    <div className="prorroga-submit-container">
                      <Button 
                        color="primary"
                        className="prorroga-add-comment-btn"
                        onClick={addComment}
                        disabled={!newComment.trim()}
                        block
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        {!selectedProrroga.is_open ? 'Reabrir y Agregar Comentario' : 'Agregar Comentario'}
                      </Button>
                    </div>
                  </FormGroup>
                </div>
              ) : (
                <div className="prorroga-visualization-only-notice">
                  <FontAwesomeIcon icon={faEye} className="mr-2" />
                  Solo visualización
                </div>
              )}
            </div>
          </div>
        )}
      </BaseModal>
    </PageContainer>
  );
};

SolicitudProrroga.propTypes = {
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

SolicitudProrroga.defaultProps = {
  client: null,
};

export default SolicitudProrroga;
